import { Injectable } from '@angular/core';
import { UserStatus } from '../enums/UserStatus.enum';
import { IdType } from '../enums/IdType.enum';
import { Roles } from '../enums/Roles.enum';
import { FeatureFlagService } from './feature-flag.service';
import { LaftService } from './laft.service';
import { LaftStatusBannerService } from './laft-status-banner.service';

/**
 * FIN-1709 · what an inversionista may do with money, in one place.
 *
 * The same condition used to be written out three times — the route guard, the side menu
 * and the *Añadir fondos* button — and each copy answered a different question by
 * accident. This ticket opens the deposit funnel to the pre-approved, so all three had to
 * change together; a fourth copy is how one of them ends up out of step with the others.
 *
 * The rows below are the capability matrix, in order, first match wins. Reading them as
 * rows is the point: the matrix is the specification, and a chain of nested conditions
 * hides which state it is describing.
 */
export interface MoneyCapabilities {
  bankAccount: boolean;
  deposit: boolean;
  invest: boolean;
  withdraw: boolean;
  donate: boolean;
}

const ALL_CAPABILITIES: MoneyCapabilities = {
  bankAccount: true,
  deposit: true,
  invest: true,
  withdraw: true,
  donate: true,
};

const NO_CAPABILITIES: MoneyCapabilities = {
  bankAccount: false,
  deposit: false,
  invest: false,
  withdraw: false,
  donate: false,
};

// FIN-1709 · the pre-approved row. Retiro is open on Mariela's answer, recorded on the
// ticket; investing is the one capability pre-approval deliberately does not grant.
//
// ⚠ `donate` stays shut. The ticket does not open it and the capability matrix does not
// give it a column, so it keeps the answer the blanket rule gives this state today —
// the alternative is opening a money path nobody asked for.
const PRE_APPROVED_CAPABILITIES: MoneyCapabilities = {
  bankAccount: true,
  deposit: true,
  invest: false,
  withdraw: true,
  donate: false,
};

// The statuses an inversionista holds while pre-approved: `email verified` before signing
// and `pending` after it. Signing must not cost a capability they already had.
const PRE_APPROVED_STATUSES: string[] = [
  UserStatus['Email Verified'],
  UserStatus.Pending,
];

const NO_PRE_APPROVAL_CAPABILITIES: MoneyCapabilities = {
  bankAccount: true,
  deposit: true,
  invest: false,
  withdraw: false,
  donate: false,
};

const AWAITING_LAFT_APPROVAL_STATUSES: string[] = [
  UserStatus['Email Verified'],
  UserStatus.Pending,
];

const INTERNAL_ROLES: string[] = [
  Roles.ADMIN,
  Roles.SUPER_ADMIN,
  Roles.RISK_ANALYST,
  Roles.CLIENT_HAPPINESS,
  Roles.ACCOUNT_MANGER,
  Roles.ACCOUNT_MANAGER_SUPERVISOR,
  Roles.INVESTOR_SALES,
  Roles.INVESTOR_SALES_SUPERVISOR,
];

@Injectable({
  providedIn: 'root',
})
export class InvestorCapabilitiesService {
  constructor(
    private featureFlagService: FeatureFlagService,
    private laftService: LaftService,
    private laftStatusBanner: LaftStatusBannerService
  ) {}

  capabilitiesFor(profile: any): MoneyCapabilities {
    // Nothing loaded yet: the guard has always let the navigation through rather than
    // bouncing someone whose profile simply has not arrived.
    if (!profile || !profile.status) {
      return ALL_CAPABILITIES;
    }

    if (INTERNAL_ROLES.includes(profile.role) || profile.role === Roles.CLIENT) {
      return ALL_CAPABILITIES;
    }

    // Flag off, nothing below the early-deposit rows applies. There is no early
    // `matrixScore` to be pre-approved on either — FIN-1703 only writes it at form submit
    // when the flag is on — so this is the whole of today's behaviour, unchanged.
    if (!this.featureFlagService.isEarlyDepositEnabled()) {
      return this.legacyCapabilities(profile);
    }

    // Persona jurídica is not part of this project: the RUC flow keeps the blanket rule.
    if (profile.idType === IdType.RUC) {
      return this.legacyCapabilities(profile);
    }

    if (profile.status === UserStatus.Approved) {
      return ALL_CAPABILITIES;
    }

    // ⚠ `isBlocked` is checked here and nowhere else this ticket did not already touch.
    // These capabilities are shut today, so opening them without asking would hand the
    // funnel to a blocked inversionista — a door the blanket rule currently closes.
    if (
      profile.isPreApproved &&
      !profile.isBlocked &&
      PRE_APPROVED_STATUSES.includes(profile.status)
    ) {
      return PRE_APPROVED_CAPABILITIES;
    }

    // The banner offers this deposit off the declaration while the matrix answers off
    // `users.status`; without this row the guard refuses the funnel it points at (D118).
    if (
      profile.isPreApproved !== true &&
      !profile.isBlocked &&
      AWAITING_LAFT_APPROVAL_STATUSES.includes(profile.status) &&
      this.hasSignedAndValidatedDeclaration(profile)
    ) {
      return NO_PRE_APPROVAL_CAPABILITIES;
    }

    return this.legacyCapabilities(profile);
  }

  private hasSignedAndValidatedDeclaration(profile: any): boolean {
    // The cache is empty on a page load the fetch never ran for; the profile carries the
    // same two facts and is the fallback that survives it.
    const declaration = this.laftStatusBanner.effectiveDeclaration(
      profile,
      this.laftService.currentDeclaration
    );

    return (
      declaration?.keynuaSignedContract === true &&
      this.laftStatusBanner.isValidationDone(declaration)
    );
  }

  /**
   * The single blanket check that gated all four capabilities before this ticket, kept
   * verbatim so every state the matrix marks as a regression behaves exactly as it does
   * today — in particular the inversionista who signed without being pre-approved.
   *
   * ⚠ `isBlocked` is deliberately not consulted here. The pre-approved row above is the
   * only place this service consults it, and adding it here would be new behaviour this
   * ticket was not asked for.
   *
   * ⚠ FIN-1711 removed FIN-1714's retiro-only row, which sat above the `approved` row and
   * gave a disapproved inversionista `withdraw`. A disapproved account can no longer log in
   * at all, so there is no session left to hold a capability — the standing had nothing to
   * stand on. Disapproved therefore falls through to this blanket rule again, exactly as it
   * did before FIN-1714.
   */
  private legacyCapabilities(profile: any): MoneyCapabilities {
    const laftEnabledForIdType =
      profile.idType === IdType.RUC
        ? this.featureFlagService.isLAFTJuridicaEnabled()
        : this.featureFlagService.isLAFTEnabled();

    // `incompleteProfile` is only ever written as a boolean, so its presence is what the
    // original condition tested — the guard and the menu by `hasOwnProperty`, the button
    // by `typeof === 'boolean'`. The two agree on every value the backend produces.
    const hasIncompleteProfileFlag =
      profile.hasOwnProperty('incompleteProfile') &&
      profile.incompleteProfile !== undefined;

    const isProfileIncomplete =
      profile.status !== UserStatus.Approved &&
      laftEnabledForIdType &&
      hasIncompleteProfileFlag;

    return isProfileIncomplete ? NO_CAPABILITIES : ALL_CAPABILITIES;
  }
}
